// staffApi.ts
import { QueryParams, TAG_TYPES } from 'types';
import { baseApi } from './baseApi';
import { Staff as T } from 'models';
import { getQueryParams } from 'utils';

const urlEndpoint = `${process.env.REACT_APP_BASE_URL}/staffs`;
const tagType = TAG_TYPES.Staff;
// Create staff-specific API service
// const staffApiService = createApiService<Staff>(URL_STAFF, TAG_TYPES.Staff);

// You can add staff-specific custom endpoints here if needed
// Example: extending with a custom endpoint
export const staffApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllStaffs: builder.query<
      {
        data: {
          rows: T[];
          count: number;
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const query = getQueryParams(params?.query);
        if (query === '') return { url: urlEndpoint, method: 'GET' };
        return {
          url: `${urlEndpoint}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [...(result?.data.rows || []).map(({ id }) => ({ type: tagType, id })), { type: tagType, id: 'LIST' }]
          : [{ type: tagType, id: 'LIST' }];
      },
    }),

    getByIdStaff: builder.query<
      {
        data: T;
        success: boolean;
      },
      { id: string; query: QueryParams }
    >({
      query: (params) => ({
        url: `${urlEndpoint}/${params.id}?${params.query ? getQueryParams(params.query) : ''}`,
        method: 'GET',
      }),
      providesTags: (result, error, params) => [{ type: tagType, id: params.id }],
    }),
    createStaff: builder.mutation<T, Omit<T, 'id'>>({
      query: (data) => ({
        url: urlEndpoint,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: tagType, id: 'LIST' }],
    }),

    updateStaff: builder.mutation<T, Partial<T> & { id: string }>({
      query: ({ id, ...patch }) => ({
        url: `${urlEndpoint}/${id}`,
        method: 'PUT',
        data: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
      ],
    }),

    deleteStaff: builder.mutation<void, string>({
      query: (id) => ({
        url: `${urlEndpoint}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
      ],
    }),
    // Example: custom endpoint for staff
    getActiveStaffs: builder.query<T[], void>({
      query: () => ({
        url: `${urlEndpoint}/active`,
        method: 'GET',
      }),
      providesTags: [{ type: TAG_TYPES.Staff, id: 'ACTIVE' }],
    }),
  }),
});
// Export hooks with more descriptive names
export const {
  useGetAllStaffsQuery: useGetStaffsQuery,
  useLazyGetAllStaffsQuery: useLazyGetStaffsQuery,
  useGetByIdStaffQuery: useGetStaffByIdQuery,
  useCreateStaffMutation: useCreateStaffMutation,
  useUpdateStaffMutation: useUpdateStaffMutation,
  useDeleteStaffMutation: useDeleteStaffMutation,
  useGetActiveStaffsQuery: useGetActiveStaffsQuery,
} = staffApiService;
